/**
 *
 * Regions - Page
 *
 */

import { BasicTable, IBasicTableState } from 'app/components/BasicTable';
import * as React from 'react';
import { withSavedHistoryT } from 'app/components/BasicTable/withSavedHistory';
import {
  selectAuthenticatedUser,
  selectglobalSettings,
} from 'app/slice/selectors';
import { Roles } from 'app/slice/types';
import { ScreensId } from 'enums/ConfigurableTypes';
import { translations } from 'locales/translations';
import { intersection } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AuthenticatedUser } from 'types/AuthenticatedUser';
import { getColumns } from './Columns';
import {
  RowActionsMenu,
  RowActionsProps,
} from 'app/components/BasicTable/RowActions';
import { CellRenderer } from 'app/components/BasicTable/BasicTableProps';
import { AxiosError } from 'axios';
import { httpClient } from 'api/HttpClient';
import { useAppSettingsSlice } from 'app/slice';
import { PageActionRenderer } from 'app/components/BasicTable/PageActions/PageActionsRenderer';
import { IBasePageProps, IListPageProps } from 'app/pages/IBasePageProps';
import { GePageActions } from './Actions/GetPageActions';
import { ISuperGroupDto } from '../ISuperGroupDto';
import { GetFilters } from './Filters';

// IBasePageProps is for Routing
// IListPageProps is for filtering has appliedFiler?: IFilterSettings<T>;
export interface SuperGroupsPageProps
  extends IBasePageProps,
    IListPageProps<ISuperGroupDto> {}

export function SuperGroupsPage(props: SuperGroupsPageProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { actions: notifyActions } = useAppSettingsSlice();
  //USER AUTHENTICATION
  const User: AuthenticatedUser | undefined = useSelector(
    selectAuthenticatedUser,
  );
  const roles: number[] | undefined = User?.Roles;
  const allowedroles: Roles[] = [Roles.Administrators];
  const IsAdmin: boolean = intersection(roles, allowedroles).length > 0;
  const settings = useSelector(selectglobalSettings);
  //COLUMNS
  const columns = useMemo(() => {
    return getColumns(t, settings, IsAdmin);
  }, [IsAdmin, settings, t]);

  //PAGE ACTIONS
  const PageActions: PageActionRenderer[] = GePageActions({
    isAdmin: IsAdmin,
  });

  //TABLE-INITIAL-STATE
  const initialState: IBasicTableState<ISuperGroupDto> = useMemo(
    () => ({
      sortBy: [{ id: 'Id', desc: false }],
    }),
    [],
  );
  //SELECTED ROWS ACTIONS
  const rowActions = React.useMemo<CellRenderer<ISuperGroupDto>[]>(() => {
    const result = IsAdmin
      ? [
          ({ row, onRefresh, ...rest }) => {
            const items: RowActionsProps[] = [];
            const handleDeleteClick = async () => {
              const url = `/api/odata/v4/SuperGroup(${row.original.Id})`;
              try {
                await httpClient.delete(url).then(response => {
                  if ((response as any).ErrorMessages.length > 0) {
                    (response as any).ErrorMessages.forEach(errmsg =>
                      dispatch(
                        notifyActions.addNotification({
                          variant: 'error',
                          message: errmsg,
                        }),
                      ),
                    );
                  } else {
                    dispatch(
                      notifyActions.addNotification({
                        message: t(translations.Success),
                        variant: 'success',
                      }),
                    );
                  }
                });
                onRefresh();
              } catch (error: unknown) {
                const responsemessage = (error as AxiosError)?.response?.data
                  ?.error?.innererror?.message as string;
                const message =
                  responsemessage === undefined
                    ? t(translations.err_ErrorOccured)
                    : responsemessage;
                dispatch(
                  notifyActions.addNotification({
                    message: message,
                    variant: 'error',
                  }),
                );
              }
            };
            items.push({
              onClick: handleDeleteClick,
              text: t(translations.Delete) as string,
              icon: 'trash',
            });

            return (
              <RowActionsMenu id={`sgroup_${row.original.Id}`} items={items} />
            );
          },
        ]
      : [];
    return result;
  }, [IsAdmin, dispatch, notifyActions, t]);

  const url = '/api/odata/v4/SuperGroup';
  //RENDER
  return (
    <>
      <TTable
        screenId={ScreensId.SuperGroup}
        screenName={t(translations.menu_Divisions)}
        api={url}
        columns={columns}
        initialState={initialState}
        searchColumns={['Name', 'Description', 'RegionName']}
        additionalColumns={['RegionId', 'AssetId']}
        rowActions={rowActions}
        pageActions={PageActions}
        serviceGroups={[]}
      />
    </>
  );
}

const TTable = withSavedHistoryT<ISuperGroupDto>(
  BasicTable,
  '/SuperGroups',
  GetFilters,
);
